import { enqueueSnackbar } from 'notistack';
import React, { createContext, useEffect, useRef, useState } from 'react';
import { IntlProvider } from 'react-intl';

import { ensureBSCTestnet, isBSCTestnet } from '../helper/networkUtils';
import English from '../lang/en.json';
import Turkish from '../lang/tr.json';

// Define types for our messages
type Messages = Record<string, string>;

// Define the context type
interface ContextType {
    locale: string;
    selectLanguage: (newLocale: string) => void;
}

// Create context with default values
export const Context = createContext<ContextType>({
    locale: 'en',
    selectLanguage: (newLocale: string) => {},
});

// Get browser language
const local = navigator.language;

// Set default language based on browser language
let lang: Messages;
if (local === 'tr') {
    lang = Turkish;
} else {
    lang = English;
}

// Define props without using React.FC
interface WrapperProps {
    children: React.ReactNode;
}

// Use regular function declaration without React.FC
const Wrapper = ({ children }: WrapperProps) => {
    const [locale, setLocale] = useState<string>(local);
    const [messages, setMessages] = useState<Messages>(lang);
    const toastShownRef = useRef<boolean>(false);
    const isSwitchingRef = useRef<boolean>(false);
    const chainChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize toast shown flag from localStorage to persist across page navigations
    useEffect(() => {
        const hasShownToast =
            localStorage.getItem('bscTestnetToastShown') === 'true';
        toastShownRef.current = hasShownToast;
    }, []);

    function selectLanguage(newLocale: string): void {
        setLocale(newLocale);
        if (newLocale === 'en') {
            setMessages(English);
        } else {
            setMessages(Turkish);
        }
    }

    // Auto-switch to BSC Testnet when app loads (only for MetaMask users)
    useEffect(() => {
        const switchToCorrectNetwork = async () => {
            try {
                // Check if user is using Web2 (Thirdweb) - skip network switching for Web2 users
                const isWeb2User =
                    localStorage.getItem('isWeb2User') === 'true' ||
                    localStorage.getItem('usingThirdweb') === 'true';

                // Only switch network for MetaMask/Web3 users, not Web2 users
                if (isWeb2User) {
                    return;
                }

                // Check if MetaMask is installed
                if (
                    typeof window.ethereum !== 'undefined' &&
                    window.ethereum.isMetaMask
                ) {
                    // Check if already on correct network
                    const isOnCorrectNetwork = await isBSCTestnet();

                    if (isOnCorrectNetwork) {
                        // Already on correct network - show success only once
                        if (!toastShownRef.current) {
                            enqueueSnackbar('Connected to BSC Testnet', {
                                variant: 'success',
                            });
                            toastShownRef.current = true;
                            localStorage.setItem(
                                'bscTestnetToastShown',
                                'true'
                            );
                        }
                        return;
                    }

                    // Mark that we're programmatically switching
                    isSwitchingRef.current = true;

                    // Automatically switch to BSC Testnet
                    await ensureBSCTestnet();

                    // Reset switching flag after a delay to allow chainChanged to handle it
                    setTimeout(() => {
                        isSwitchingRef.current = false;
                    }, 2000);

                    // Verify the switch was successful
                    const switchedSuccessfully = await isBSCTestnet();
                    if (switchedSuccessfully) {
                        if (!toastShownRef.current) {
                            enqueueSnackbar(
                                'Successfully switched to BSC Testnet',
                                {
                                    variant: 'success',
                                }
                            );
                            toastShownRef.current = true;
                            localStorage.setItem(
                                'bscTestnetToastShown',
                                'true'
                            );
                        }
                    } else {
                        if (!toastShownRef.current) {
                            enqueueSnackbar(
                                'You are connected to the wrong network. Please switch to BSC Testnet.',
                                {
                                    variant: 'error',
                                }
                            );
                            toastShownRef.current = true;
                        }
                    }
                }
            } catch (error: any) {
                // Reset switching flag on error
                isSwitchingRef.current = false;

                // Show error toast if network switching fails
                // Check if user rejected the switch
                if (error?.code === 4001) {
                    if (!toastShownRef.current) {
                        enqueueSnackbar(
                            'Network switch was rejected. Please switch to BSC Testnet manually.',
                            {
                                variant: 'warning',
                            }
                        );
                        toastShownRef.current = true;
                    }
                } else {
                    // Check if still on wrong network
                    const isOnCorrectNetwork = await isBSCTestnet().catch(
                        () => false
                    );
                    if (!isOnCorrectNetwork) {
                        if (!toastShownRef.current) {
                            enqueueSnackbar(
                                'You are connected to the wrong network. Please switch to BSC Testnet to continue.',
                                {
                                    variant: 'error',
                                }
                            );
                            toastShownRef.current = true;
                        }
                    } else {
                        const errorMessage =
                            error?.message || 'Network switch failed';
                        if (!toastShownRef.current) {
                            enqueueSnackbar(`Error: ${errorMessage}`, {
                                variant: 'error',
                            });
                            toastShownRef.current = true;
                        }
                    }
                }
            }
        };

        // Run network switching when component mounts with a small delay
        const timeoutId = setTimeout(() => {
            switchToCorrectNetwork();
        }, 500);

        // Also listen for network changes and switch if user changes network manually
        if (typeof window.ethereum !== 'undefined') {
            // Type assertion for event listener methods (MetaMask EIP-1193 provider)
            const ethereum = window.ethereum as any;

            const handleChainChanged = async () => {
                // Clear any pending timeout
                if (chainChangeTimeoutRef.current) {
                    clearTimeout(chainChangeTimeoutRef.current);
                }

                // Debounce the chain change handler to prevent multiple rapid calls
                chainChangeTimeoutRef.current = setTimeout(async () => {
                    try {
                        const isWeb2User =
                            localStorage.getItem('isWeb2User') === 'true' ||
                            localStorage.getItem('usingThirdweb') === 'true';

                        if (isWeb2User) {
                            return;
                        }

                        // If we just programmatically switched, don't show toast again
                        // The initial switch function already handled it
                        if (isSwitchingRef.current) {
                            // Reset the flag after checking
                            setTimeout(() => {
                                isSwitchingRef.current = false;
                            }, 1000);
                            return;
                        }

                        // Small delay to ensure network change is complete
                        await new Promise((resolve) =>
                            setTimeout(resolve, 500)
                        );

                        // Check if user switched to correct network
                        const isOnCorrectNetwork = await isBSCTestnet();

                        if (isOnCorrectNetwork) {
                            if (!toastShownRef.current) {
                                enqueueSnackbar('Connected to BSC Testnet', {
                                    variant: 'success',
                                });
                                toastShownRef.current = true;
                                localStorage.setItem(
                                    'bscTestnetToastShown',
                                    'true'
                                );
                            }
                            return;
                        }

                        // User switched to wrong network - try to switch back
                        isSwitchingRef.current = true;
                        await ensureBSCTestnet();

                        // Reset switching flag
                        setTimeout(() => {
                            isSwitchingRef.current = false;
                        }, 2000);

                        // Verify the switch was successful
                        const switchedSuccessfully = await isBSCTestnet();
                        if (switchedSuccessfully) {
                            if (!toastShownRef.current) {
                                enqueueSnackbar(
                                    'Successfully switched to BSC Testnet',
                                    {
                                        variant: 'success',
                                    }
                                );
                                toastShownRef.current = true;
                                localStorage.setItem(
                                    'bscTestnetToastShown',
                                    'true'
                                );
                            }
                        } else {
                            if (!toastShownRef.current) {
                                enqueueSnackbar(
                                    'You are connected to the wrong network. Please switch to BSC Testnet to continue.',
                                    {
                                        variant: 'error',
                                    }
                                );
                                toastShownRef.current = true;
                            }
                        }
                    } catch (error: any) {
                        // Reset switching flag on error
                        isSwitchingRef.current = false;

                        // Check if user rejected the switch
                        if (error?.code === 4001) {
                            if (!toastShownRef.current) {
                                enqueueSnackbar(
                                    'Network switch was rejected. Please switch to BSC Testnet manually.',
                                    {
                                        variant: 'warning',
                                    }
                                );
                                toastShownRef.current = true;
                            }
                        } else {
                            // Check if still on wrong network
                            const isOnCorrectNetwork =
                                await isBSCTestnet().catch(() => false);
                            if (!isOnCorrectNetwork) {
                                if (!toastShownRef.current) {
                                    enqueueSnackbar(
                                        'You are connected to the wrong network. Please switch to BSC Testnet to continue.',
                                        {
                                            variant: 'error',
                                        }
                                    );
                                    toastShownRef.current = true;
                                }
                            } else {
                                const errorMessage =
                                    error?.message || 'Network switch failed';
                                if (!toastShownRef.current) {
                                    enqueueSnackbar(`Error: ${errorMessage}`, {
                                        variant: 'error',
                                    });
                                    toastShownRef.current = true;
                                }
                            }
                        }
                    }
                }, 1000); // 1 second debounce
            };

            // Listen for chain changes (EIP-1193 event)
            if (ethereum.on) {
                ethereum.on('chainChanged', handleChainChanged);
            }

            // Cleanup listener on unmount
            return () => {
                if (chainChangeTimeoutRef.current) {
                    clearTimeout(chainChangeTimeoutRef.current);
                }
                if (ethereum && ethereum.removeListener) {
                    ethereum.removeListener('chainChanged', handleChainChanged);
                }
            };
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, []);

    return (
        <Context.Provider value={{ locale, selectLanguage }}>
            <IntlProvider messages={messages} locale={locale}>
                {children as any}
            </IntlProvider>
        </Context.Provider>
    );
};

export default Wrapper;
